import React from 'react'
import { cn } from '@/lib/utils'

export interface StatCardProps {
  /** Label text displayed above the value */
  label: string
  /** Main value to display */
  value: React.ReactNode
  /** Icon to display in the corner (optional) */
  icon?: React.ReactNode
  /** Color scheme for background and icon container. Only applies to 'compact' variant. */
  colorScheme?: 'violet' | 'purple' | 'blue' | 'green' | 'red' | 'yellow' | 'orange' | 'gray'
  /** Color scheme for icon background only - used in 'section' variant for dynamic colors */
  iconColorScheme?: 'violet' | 'purple' | 'blue' | 'green' | 'red' | 'yellow' | 'orange' | 'gray'
  /** Size variant - small for inline cards, medium for standalone */
  size?: 'small' | 'medium'
  /** Layout variant - compact for colored background cards, section for Section-wrapped cards */
  variant?: 'compact' | 'section'
  /** Loading state */
  loading?: boolean
  /** Additional class names for the value text */
  valueClassName?: string
  /** Additional class names for the container */
  className?: string
}

const colorSchemeStyles = {
  violet: {
    container: 'bg-violet-50 dark:bg-violet-500/10 border-violet-100',
    iconBg: 'bg-violet-100 dark:bg-violet-500/20',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
  purple: {
    container: 'bg-purple-50 dark:bg-purple-500/10 border-purple-100',
    iconBg: 'bg-purple-100 dark:bg-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  blue: {
    container: 'bg-blue-50 dark:bg-blue-500/10 border-blue-100',
    iconBg: 'bg-blue-100 dark:bg-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  green: {
    container: 'bg-green-50 dark:bg-green-500/10 border-green-100',
    iconBg: 'bg-green-100 dark:bg-green-500/20',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  red: {
    container: 'bg-red-50 dark:bg-red-500/10 border-red-100',
    iconBg: 'bg-red-100 dark:bg-red-500/20',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  yellow: {
    container: 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-100',
    iconBg: 'bg-yellow-100 dark:bg-yellow-500/20',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  orange: {
    container: 'bg-orange-50 dark:bg-orange-500/10 border-orange-100',
    iconBg: 'bg-orange-100 dark:bg-orange-500/20',
    iconColor: 'text-orange-600 dark:text-orange-400',
  },
  gray: {
    container: 'bg-gray-50 dark:bg-accent border-gray-100',
    iconBg: 'bg-gray-100 dark:bg-accent',
    iconColor: 'text-gray-400 dark:text-muted-foreground',
  },
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  colorScheme = 'gray',
  iconColorScheme,
  size = 'medium',
  variant = 'compact',
  loading = false,
  valueClassName,
  className,
}) => {
  const colors = colorSchemeStyles[colorScheme]
  const iconColors = iconColorScheme ? colorSchemeStyles[iconColorScheme] : colors
  const isSmall = size === 'small'

  // Section variant - used in Show.tsx with optional absolute positioned icon
  if (variant === 'section') {
    return (
      <div className={cn('relative -mt-1', className)}>
        <div className={icon ? 'pr-12' : ''}>
          <p className="text-sm text-gray-500 dark:text-muted-foreground mb-1 min-h-[2.5rem]">{label}</p>
          <p
            className={cn(
              'text-2xl font-bold text-gray-900 dark:text-foreground',
              loading && 'animate-pulse',
              valueClassName
            )}
          >
            {loading ? '...' : value}
          </p>
        </div>
        {icon && (
          <div
            className={cn(
              'absolute top-0 right-0 p-2 rounded-lg',
              iconColors.iconBg
            )}
          >
            {icon}
          </div>
        )}
      </div>
    )
  }

  // Compact variant - used in Index.tsx with colored background
  return (
    <div
      className={cn(
        'rounded-lg border',
        colors.container,
        isSmall ? 'p-3' : 'p-4',
        className
      )}
    >
      <div className="flex items-center justify-between mb-0.5">
        <p className={cn('text-gray-500 dark:text-muted-foreground', isSmall ? 'text-xs' : 'text-sm')}>
          {label}
        </p>
        {icon && (
          <div
            className={cn(
              'rounded flex-shrink-0',
              iconColors.iconBg,
              iconColors.iconColor,
              isSmall ? 'p-1' : 'p-2'
            )}
          >
            {React.isValidElement(icon)
              ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
                  className: cn(
                    isSmall ? 'h-3 w-3' : 'h-5 w-5',
                    (icon.props as { className?: string }).className
                  ),
                })
              : icon}
          </div>
        )}
      </div>
      <p
        className={cn(
          'font-bold',
          isSmall ? 'text-lg text-gray-900 dark:text-foreground' : 'text-2xl text-gray-900 dark:text-foreground',
          loading && 'animate-pulse',
          valueClassName
        )}
      >
        {loading ? '...' : value}
      </p>
    </div>
  )
}
